import { ReactReduxContext } from 'react-redux';
import { refreshUserInfoRequest } from '../../actions';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { sendRequest } from '../../utils/dateHelpers';
import {
  canonicalizeTagName,
  isSystemTag
} from '../../utils/managementNotifications';
import Loading from '../Loading';

const inputStyle =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white';
const buttonStyle =
  'rounded-lg px-3 py-2 text-sm font-medium hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-slate-700';
const errorMessage = error =>
  error?.errorData?.message ||
  error?.response?.data?.message ||
  error?.message ||
  'Please try again.';

export default function Tags() {
  const { store } = useContext(ReactReduxContext) || {};
  const [tags, setTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [inline, setInline] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const dialogRef = useRef(null);
  const returnFocus = useRef(null);

  const load = async () => {
    const result = await sendRequest('/tags/get', 'GET');
    setTags(result.data || []);
    setGroups(result.groups || []);
  };
  useEffect(() => {
    load()
      .catch(err => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!dialog) return;
    const element = dialogRef.current;
    const previous = document.activeElement;
    returnFocus.current = previous;
    element?.querySelector('input, select, button')?.focus();
    return () => previous?.focus();
  }, [Boolean(dialog)]);

  const openDialog = value => {
    setError('');
    setDialog(value);
  };
  const perform = async (url, payload, close) => {
    setBusy(true);
    setError('');
    try {
      await sendRequest(url, 'POST', payload);
      await load();
      close();
      setNotice('Changes saved.');
      store?.dispatch(refreshUserInfoRequest());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const submitDialog = event => {
    event.preventDefault();
    if (dialog.kind === 'group') {
      perform(
        dialog.item ? '/tag-groups/edit' : '/tag-groups/add',
        { name: dialog.name, groupUid: dialog.item?._id },
        () => setDialog(null)
      );
    } else if (dialog.kind === 'deleteGroup') {
      perform(
        '/tag-groups/delete',
        { groupUid: dialog.item._id, targetGroupUid: dialog.target },
        () => setDialog(null)
      );
    } else if (dialog.kind === 'deleteTag') {
      perform('/tags/delete', { tagUid: dialog.item._id }, () =>
        setDialog(null)
      );
    } else {
      perform(
        '/tags/edit',
        {
          tagUid: dialog.item._id,
          tagName: dialog.name,
          groupUid: dialog.target
        },
        () => setDialog(null)
      );
    }
  };
  const trapFocus = event => {
    if (event.key === 'Escape' && !busy) {
      setDialog(null);
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = [
      ...dialogRef.current.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled)'
      )
    ];
    const first = controls[0],
      last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  const query = search.trim().toLowerCase();
  const visibleGroups = groups.filter(
    group =>
      group.name.toLowerCase().includes(query) ||
      tags.some(
        tag =>
          tag.group_uid === group._id &&
          tag.tagName.toLowerCase().includes(query)
      )
  );
  const groupTags = group =>
    tags.filter(
      tag =>
        tag.group_uid === group._id ||
        (!tag.group_uid && group.seed_key === 'general')
    );
  if (loading) return <Loading />;
  return (
    <div className="space-y-6 text-slate-800 dark:text-slate-100">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Tags & groups
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Organize your developers into useful segments. Every tag belongs to
            a group.
          </p>
        </div>
        <button
          className="shrink-0 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => openDialog({ kind: 'group', name: '' })}
        >
          + New group
        </button>
      </div>
      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200">
        Use groups to select related tags together in report filters. Developers
        tagged <strong>Excluded</strong> are left out of analysis.
      </div>
      <div className="max-w-md">
        <label htmlFor="tag-search" className="sr-only">
          Search tags and groups
        </label>
        <input
          id="tag-search"
          type="search"
          className={inputStyle}
          placeholder="Search tags or groups…"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </div>
      <p role="status" className="sr-only">
        {notice}
      </p>
      {error && !dialog && !inline && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <div className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white dark:divide-slate-700 dark:border-slate-700 dark:bg-boxdark">
        {visibleGroups.map(group => {
          const members = groupTags(group);
          const visibleTags = group.name.toLowerCase().includes(query)
            ? members
            : members.filter(tag => tag.tagName.toLowerCase().includes(query));
          const expanded =
            Boolean(query) ||
            expandedGroups.includes(group._id) ||
            inline?.groupUid === group._id;
          return (
            <section
              key={group._id}
              aria-label={group.name}
              className="min-w-0"
            >
              <div className="flex items-center gap-2 px-3 py-2 sm:px-4">
                <h3 className="min-w-0 flex-1">
                  <button
                    type="button"
                    aria-label={`Toggle ${group.name}`}
                    aria-expanded={expanded}
                    aria-controls={`group-tags-${group._id}`}
                    className="flex w-full items-center gap-3 rounded-lg py-3 text-left focus-visible:ring-2 focus-visible:ring-primary"
                    onClick={() => {
                      if (inline?.groupUid === group._id && !busy) {
                        setInline(null);
                        setError('');
                      }
                      setExpandedGroups(previous =>
                        previous.includes(group._id)
                          ? previous.filter(uid => uid !== group._id)
                          : [...previous, group._id]
                      );
                    }}
                  >
                    <svg
                      aria-hidden="true"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                    >
                      <path d="m9 5 7 7-7 7" />
                    </svg>
                    <span className="min-w-0 font-semibold">{group.name}</span>
                    <span
                      aria-label={`${members.length} tags`}
                      className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-300"
                    >
                      {members.length}
                    </span>
                  </button>
                </h3>
                <button
                  type="button"
                  aria-label="+ Add tag"
                  title={`Add tag to ${group.name}`}
                  disabled={busy}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-xl text-slate-600 hover:border-primary hover:bg-primary/5 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                  onClick={() => {
                    setExpandedGroups(previous =>
                      previous.includes(group._id)
                        ? previous
                        : [...previous, group._id]
                    );
                    setInline({ groupUid: group._id, name: '' });
                    setError('');
                  }}
                >
                  +
                </button>
                <details className="relative">
                  <summary
                    aria-label={`Manage ${group.name}`}
                    className="cursor-pointer list-none rounded px-2 text-xl focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    ⋯
                  </summary>
                  <div className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-600 dark:bg-slate-800">
                    <button
                      className={`${buttonStyle} w-full text-left`}
                      onClick={() =>
                        openDialog({
                          kind: 'group',
                          item: group,
                          name: group.name
                        })
                      }
                    >
                      Rename group
                    </button>
                    <button
                      className={`${buttonStyle} w-full text-left text-red-600`}
                      onClick={() =>
                        openDialog({
                          kind: 'deleteGroup',
                          item: group,
                          target: '',
                          count: members.length
                        })
                      }
                    >
                      Delete group
                    </button>
                  </div>
                </details>
              </div>
              <div id={`group-tags-${group._id}`} hidden={!expanded}>
                {expanded && (
                  <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4 sm:pl-12 dark:border-slate-700 dark:bg-slate-900/20">
                    <div className="flex flex-wrap content-start gap-2">
                      {visibleTags.map(tag => (
                        <button
                          key={tag._id}
                          aria-label={`Manage ${canonicalizeTagName(tag.tagName)} tag`}
                          onClick={() =>
                            openDialog({
                              kind: 'tag',
                              item: tag,
                              name: canonicalizeTagName(tag.tagName),
                              target: group._id
                            })
                          }
                          className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm hover:border-primary hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary dark:border-slate-600 dark:bg-slate-800"
                        >
                          {canonicalizeTagName(tag.tagName)}
                          {isSystemTag(tag) && (
                            <span
                              aria-label="Protected tag"
                              className="text-xs"
                            >
                              🔒
                            </span>
                          )}
                          <span className="text-slate-400">⋯</span>
                        </button>
                      ))}
                      {!members.length && (
                        <p className="text-sm text-slate-400">
                          Add your first tag to this group.
                        </p>
                      )}
                    </div>
                    {inline?.groupUid === group._id ? (
                      <form
                        className="mt-3 max-w-md space-y-2"
                        onSubmit={event => {
                          event.preventDefault();
                          perform(
                            '/tags/add',
                            { tagName: inline.name, groupUid: group._id },
                            () => setInline(null)
                          );
                        }}
                      >
                        <label htmlFor={`new-${group._id}`} className="sr-only">
                          New tag in {group.name}
                        </label>
                        <input
                          autoFocus
                          id={`new-${group._id}`}
                          required
                          className={inputStyle}
                          placeholder="Tag name"
                          value={inline.name}
                          disabled={busy}
                          onChange={event =>
                            setInline({ ...inline, name: event.target.value })
                          }
                          onKeyDown={event => {
                            if (event.key === 'Escape' && !busy) {
                              setInline(null);
                              setError('');
                            }
                          }}
                        />
                        {error && (
                          <p role="alert" className="text-sm text-red-600">
                            {error}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={busy || !inline.name.trim()}
                            className="rounded-lg bg-primary px-3 py-2 text-sm text-white disabled:opacity-50"
                          >
                            {busy ? 'Saving…' : 'Add tag'}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            className={buttonStyle}
                            onClick={() => {
                              setInline(null);
                              setError('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : null}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
      {!visibleGroups.length && (
        <p className="py-12 text-center text-slate-500">
          {query
            ? 'No matching tags or groups.'
            : 'Create a group to get started.'}
        </p>
      )}
      {dialog && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tag-dialog-title"
            onKeyDown={trapFocus}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800"
          >
            <h3 id="tag-dialog-title" className="mb-4 text-lg font-semibold">
              {dialog.kind === 'group'
                ? dialog.item
                  ? 'Rename group'
                  : 'New group'
                : dialog.kind === 'deleteGroup'
                  ? 'Delete group'
                  : dialog.kind === 'deleteTag'
                    ? 'Delete tag'
                    : 'Edit tag'}
            </h3>
            <form onSubmit={submitDialog} className="space-y-4">
              {(dialog.kind === 'group' || dialog.kind === 'tag') && (
                <div>
                  <label htmlFor="dialog-name" className="mb-1 block text-sm">
                    Name
                  </label>
                  <input
                    id="dialog-name"
                    required
                    autoFocus
                    disabled={
                      busy ||
                      (dialog.kind === 'tag' && isSystemTag(dialog.item))
                    }
                    className={inputStyle}
                    value={dialog.name}
                    onChange={event =>
                      setDialog({ ...dialog, name: event.target.value })
                    }
                  />
                </div>
              )}
              {dialog.kind === 'tag' && isSystemTag(dialog.item) && (
                <p className="text-sm text-slate-500">
                  This tag’s name and analysis behavior are protected. You can
                  move it to another group.
                </p>
              )}
              {(dialog.kind === 'tag' ||
                (dialog.kind === 'deleteGroup' &&
                  (dialog.count > 0 || dialog.item.seed_key))) && (
                <div>
                  <label htmlFor="dialog-group" className="mb-1 block text-sm">
                    {dialog.kind === 'tag'
                      ? 'Group'
                      : `Move ${dialog.count} tags to`}
                  </label>
                  <select
                    id="dialog-group"
                    required
                    disabled={busy}
                    className={inputStyle}
                    value={dialog.target}
                    onChange={event =>
                      setDialog({ ...dialog, target: event.target.value })
                    }
                  >
                    <option value="">Select a group</option>
                    {groups
                      .filter(
                        group =>
                          dialog.kind !== 'deleteGroup' ||
                          group._id !== dialog.item._id
                      )
                      .map(group => (
                        <option key={group._id} value={group._id}>
                          {group.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              {dialog.kind === 'deleteGroup' && (
                <p className="text-sm text-slate-500">
                  Delete “{dialog.item.name}”? Its tags will be preserved.
                </p>
              )}
              {dialog.kind === 'deleteTag' && (
                <p className="text-sm text-slate-500">
                  Delete “{dialog.item.tagName}”? It will no longer be available
                  for assignment or filtering.
                </p>
              )}
              {error && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                {dialog.kind === 'tag' && !isSystemTag(dialog.item) && (
                  <button
                    type="button"
                    disabled={busy}
                    className={`${buttonStyle} mr-auto text-red-600`}
                    onClick={() =>
                      openDialog({ kind: 'deleteTag', item: dialog.item })
                    }
                  >
                    Delete tag
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  className={buttonStyle}
                  onClick={() => setDialog(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {busy
                    ? 'Saving…'
                    : dialog.kind.startsWith('delete')
                      ? 'Delete'
                      : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
